import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  organizationService,
  Membership,
  OrganizationStats,
  Invitation,
  OrganizationData,
  OrganizationIntakePayload,
} from '../services/organizationService';
import { useToast } from '../contexts/ToastContext';
import { ORGANIZATION_KEYS, SUBSCRIPTION_KEYS, USER_KEYS } from '../constants/queryKeys';
import { getApiErrorMessage } from '../utils/apiErrorUtils';
import { uiLogger } from '../utils/loggerInstance';
import type { AgenticBusinessRoleNullable } from '../schemas/agentic';

/**
 * Centralized error handler for mutations
 * Extracts error message from axios error response or uses default message
 */
const handleMutationError = (
  error: unknown,
  defaultMessage: string,
  showError: (msg: string) => void,
) => {
  uiLogger.error(defaultMessage, { error });
  let message = defaultMessage;
  if (axios.isAxiosError(error) && error.response?.data) {
    const apiMsg = getApiErrorMessage(error.response.data);
    if (apiMsg) message = apiMsg;
  }
  showError(message);
};

export function useOrganization(enabled: boolean = true) {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.all,
    queryFn: organizationService.getOrganization,
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useOrganizationIntake(orgId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: [...ORGANIZATION_KEYS.byId(orgId || 'unknown'), 'intake'],
    queryFn: () => organizationService.getIntake(orgId!),
    enabled: !!orgId && enabled,
  });
}

export function useUpsertOrganizationIntake() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ orgId, payload }: { orgId: string; payload: OrganizationIntakePayload }) =>
      organizationService.upsertIntake(orgId, payload),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: [...ORGANIZATION_KEYS.byId(orgId), 'intake'] });
      showSuccess('Organization setup saved');
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to save organization setup', showError);
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({
      orgId,
      data,
    }: {
      orgId: string;
      data: Parameters<typeof organizationService.updateOrganization>[1];
    }) => organizationService.updateOrganization(orgId, data),
    onSuccess: (data, { orgId }) => {
      // Check if the response is OrganizationData before updating cache
      if (organizationService.isOrganizationData(data)) {
        // Update cache with new data
        queryClient.setQueryData(ORGANIZATION_KEYS.byId(orgId), data);
        // Also update the main organization query as that's what most components use
        queryClient.setQueryData(ORGANIZATION_KEYS.all, data);
      } else {
        // It's a MessageResponse, so invalidate to refetch fresh data
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.byId(orgId) });
      }
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.outputSettings(orgId) });
      showSuccess('Organization updated successfully');
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to update organization details', showError);
    },
  });
}

export function useOrganizationMembers(orgId: string) {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.members(orgId),
    queryFn: () => organizationService.getMembers(orgId),
    enabled: !!orgId,
  });
}

export function useUpdateMemberAgenticBusinessRole() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({
      orgId,
      userId,
      agenticBusinessRole,
    }: {
      orgId: string;
      userId: string;
      agenticBusinessRole: AgenticBusinessRoleNullable;
      suppressToast?: boolean;
      /** When true (e.g. bulk apply), skip per-row invalidate; caller invalidates once after batch. */
      suppressInvalidation?: boolean;
    }) => organizationService.updateMemberAgenticBusinessRole(orgId, userId, agenticBusinessRole),
    // Optimistic update
    onMutate: async ({ orgId, userId, agenticBusinessRole }) => {
      await queryClient.cancelQueries({ queryKey: ORGANIZATION_KEYS.members(orgId) });
      const previousMembers = queryClient.getQueryData<Membership[]>(
        ORGANIZATION_KEYS.members(orgId),
      );

      if (previousMembers) {
        queryClient.setQueryData<Membership[]>(
          ORGANIZATION_KEYS.members(orgId),
          previousMembers.map((member) =>
            member.id === userId
              ? { ...member, agentic_business_role: agenticBusinessRole }
              : member,
          ),
        );
      }

      return { previousMembers };
    },
    onSuccess: (_, { orgId, suppressToast, suppressInvalidation }) => {
      if (!suppressInvalidation) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.members(orgId) });
      }
      if (!suppressToast) {
        showSuccess('User profile updated');
      }
    },
    onError: (error: unknown, { orgId, suppressToast }, context) => {
      if (context?.previousMembers) {
        queryClient.setQueryData(ORGANIZATION_KEYS.members(orgId), context.previousMembers);
      }
      if (!suppressToast) {
        handleMutationError(error, 'Failed to update user profile', showError);
      }
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({
      orgId,
      userId,
      role,
    }: {
      orgId: string;
      userId: string;
      role: 'admin' | 'member';
      suppressToast?: boolean;
      suppressInvalidation?: boolean;
    }) => organizationService.updateMemberRole(orgId, userId, role),
    // Optimistic update
    onMutate: async ({ orgId, userId, role }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ORGANIZATION_KEYS.members(orgId) });

      // Snapshot previous value
      const previousMembers = queryClient.getQueryData<Membership[]>(
        ORGANIZATION_KEYS.members(orgId),
      );

      // Optimistically update
      if (previousMembers) {
        queryClient.setQueryData<Membership[]>(
          ORGANIZATION_KEYS.members(orgId),
          previousMembers.map((member) => (member.id === userId ? { ...member, role } : member)),
        );
      }

      return { previousMembers };
    },
    onSuccess: (_, { orgId, role, suppressToast, suppressInvalidation }) => {
      if (!suppressInvalidation) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.members(orgId) });
      }
      if (!suppressToast) {
        const roleName = role === 'admin' ? 'Admin' : 'Member';
        showSuccess(`Member role updated to ${roleName}`);
      }
    },
    onError: (error: unknown, { orgId, suppressToast }, context) => {
      // Rollback on error
      if (context?.previousMembers) {
        queryClient.setQueryData(ORGANIZATION_KEYS.members(orgId), context.previousMembers);
      }
      if (!suppressToast) {
        handleMutationError(error, 'Failed to update member role', showError);
      }
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({
      orgId,
      userId,
    }: {
      orgId: string;
      userId: string;
      suppressToast?: boolean;
      suppressInvalidation?: boolean;
    }) => organizationService.removeMember(orgId, userId),
    // Optimistic update
    onMutate: async ({ orgId, userId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ORGANIZATION_KEYS.members(orgId) });
      await queryClient.cancelQueries({ queryKey: ORGANIZATION_KEYS.stats(orgId) });

      // Snapshot previous values
      const previousMembers = queryClient.getQueryData<Membership[]>(
        ORGANIZATION_KEYS.members(orgId),
      );
      const previousStats = queryClient.getQueryData<OrganizationStats>(
        ORGANIZATION_KEYS.stats(orgId),
      );

      // Optimistically update members
      if (previousMembers) {
        queryClient.setQueryData<Membership[]>(
          ORGANIZATION_KEYS.members(orgId),
          previousMembers.filter((member) => member.id !== userId),
        );
      }

      // Optimistically update stats
      if (previousStats) {
        queryClient.setQueryData<OrganizationStats>(ORGANIZATION_KEYS.stats(orgId), {
          ...previousStats,
          total_active_users: Math.max(0, previousStats.total_active_users - 1),
        });
      }

      return { previousMembers, previousStats };
    },
    onSuccess: (_, { orgId, suppressToast, suppressInvalidation }) => {
      if (!suppressInvalidation) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.members(orgId) });
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.stats(orgId) });
      }
      if (!suppressToast) {
        showSuccess('Member removed successfully');
      }
    },
    onError: (error: unknown, { orgId, suppressToast }, context) => {
      // Rollback on error
      if (context?.previousMembers) {
        queryClient.setQueryData(ORGANIZATION_KEYS.members(orgId), context.previousMembers);
      }
      if (context?.previousStats) {
        queryClient.setQueryData(ORGANIZATION_KEYS.stats(orgId), context.previousStats);
      }
      if (!suppressToast) {
        handleMutationError(error, 'Failed to remove member', showError);
      }
    },
  });
}

export function useOrganizationStats(orgId: string) {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.stats(orgId),
    queryFn: () => organizationService.getStats(orgId),
    enabled: !!orgId,
  });
}

export function useTransferOwnership() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ orgId, newOwnerEmail }: { orgId: string; newOwnerEmail: string }) =>
      organizationService.transferOwnership(orgId, newOwnerEmail),
    onSuccess: (_, { orgId }) => {
      // Invalidate organization details and members (roles will change)
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.byId(orgId) });
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.members(orgId) });
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
      showSuccess('Ownership transferred successfully');
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to transfer ownership', showError);
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ orgId }: { orgId: string }) => organizationService.deleteOrganization(orgId),
    onSuccess: () => {
      // Clear org data so components stop using it; subscription-for-access uses org to choose API
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.forAccessPrefix });
      showSuccess('Organization deleted successfully');
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to delete organization', showError);
    },
  });
}

export function useOrganizationLogo(orgId: string, hasLogo: boolean) {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.logo(orgId),
    queryFn: () => organizationService.getLogo(orgId),
    enabled: !!orgId && hasLogo,
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ orgId, file }: { orgId: string; file: File }) =>
      organizationService.uploadLogo(orgId, file),
    onSuccess: (_, { orgId, file }) => {
      // 1. Optimistically update 'all' keys (main organization data)
      queryClient.setQueryData<OrganizationData>(ORGANIZATION_KEYS.all, (old) => {
        if (!old) return old;
        return { ...old, has_logo: true };
      });

      // Also update byId to be safe
      queryClient.setQueryData<OrganizationData>(ORGANIZATION_KEYS.byId(orgId), (old) => {
        if (!old) return old;
        return { ...old, has_logo: true };
      });

      // 2. Optimistically update the logo blob
      queryClient.setQueryData(ORGANIZATION_KEYS.logo(orgId), file);

      // 3. Invalidate to sync with backend
      // We purposefully DO NOT invalidate 'all' or 'byId' immediately here to prevent
      // race conditions where the backend response (potentially lagging) overwrites
      // our optimistic update. The cache will naturally refresh on window focus or stale timeout.
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.logo(orgId) });
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.outputSettings(orgId) });

      showSuccess('Logo uploaded successfully');
    },
    onError: (error: unknown, { orgId }) => {
      // Invalidate on error to revert optimistic updates
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.logo(orgId) });
      handleMutationError(error, 'Failed to upload logo', showError);
    },
  });
}

export function useDeleteLogo() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ orgId }: { orgId: string }) => organizationService.deleteLogo(orgId),
    onSuccess: (_, { orgId }) => {
      // 1. Optimistically update 'all' keys
      queryClient.setQueryData<OrganizationData>(ORGANIZATION_KEYS.all, (old) => {
        if (!old) return old;
        return { ...old, has_logo: false };
      });

      // 2. Clear logo blob
      queryClient.setQueryData(ORGANIZATION_KEYS.logo(orgId), null);

      // 3. Invalidate to sync
      // Similarly, prevent race conditions by relying on optimistic update
      // queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.byId(orgId) });
      // queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.outputSettings(orgId) });
      showSuccess('Logo deleted successfully');
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to delete logo', showError);
    },
  });
}

export function useCreateInvitation() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({
      orgId,
      email,
      role,
    }: {
      orgId: string;
      email: string;
      role: 'admin' | 'member';
    }) => organizationService.createInvitation(orgId, email, role),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.invitations(orgId) });
      showSuccess('Invitation sent successfully');
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to send invitation', showError);
    },
  });
}

export function usePendingInvitations(orgId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ORGANIZATION_KEYS.invitations(orgId),
    queryFn: () => organizationService.getPendingInvitations(orgId),
    enabled: !!orgId && enabled,
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ orgId, invitationId }: { orgId: string; invitationId: string }) =>
      organizationService.resendInvitation(orgId, invitationId),
    onSuccess: (_, { orgId }) => {
      // Invalidate invitations to refresh the list
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.invitations(orgId) });
      showSuccess('Invitation resent successfully');
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to resend invitation', showError);
    },
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({
      orgId,
      invitationId,
    }: {
      orgId: string;
      invitationId: string;
      suppressToast?: boolean;
      suppressInvalidation?: boolean;
    }) => organizationService.revokeInvitation(orgId, invitationId),
    // Optimistic update
    onMutate: async ({ orgId, invitationId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ORGANIZATION_KEYS.invitations(orgId) });

      // Snapshot previous value
      const previousInvitations = queryClient.getQueryData<Invitation[]>(
        ORGANIZATION_KEYS.invitations(orgId),
      );

      // Optimistically update
      if (previousInvitations) {
        queryClient.setQueryData<Invitation[]>(
          ORGANIZATION_KEYS.invitations(orgId),
          previousInvitations.filter((invitation) => invitation.id !== invitationId),
        );
      }

      return { previousInvitations };
    },
    onSuccess: (_, { orgId, suppressToast, suppressInvalidation }) => {
      if (!suppressInvalidation) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.invitations(orgId) });
      }
      if (!suppressToast) {
        showSuccess('Invitation revoked successfully');
      }
    },
    onError: (error: unknown, { orgId, suppressToast }, context) => {
      // Rollback on error
      if (context?.previousInvitations) {
        queryClient.setQueryData(ORGANIZATION_KEYS.invitations(orgId), context.previousInvitations);
      }
      if (!suppressToast) {
        handleMutationError(error, 'Failed to revoke invitation', showError);
      }
    },
  });
}

export function useAcceptInvitation() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => organizationService.acceptInvitation(token),
    onSuccess: () => {
      showSuccess('Invitation accepted successfully');
      // Invalidate organization so RequireBasePlanRoute and others see new org/role
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEYS.all });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEYS.forAccessPrefix });
      queryClient.invalidateQueries({ queryKey: USER_KEYS.profile() });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to accept invitation', showError);
    },
  });
}
